import React, { useEffect, useState } from "react";
import { api, type ReelScene } from "../../api";

type Props = {
  open: boolean;
  onClose: () => void;
  onResult: (title: string, scenes: ReelScene[]) => void;
};

const EXAMPLES: Array<{ label: string; prompt: string }> = [
  {
    label: "Hook für Pizza-Restaurants",
    prompt:
      "15s Reel für Pizzeria-Inhaber in Berlin. Hook über versteckte 30 % Provision bei Lieferdiensten. Promise: Allyver = 0 % Provision die ersten 90 Tage. Outro mit allyver.com.",
  },
  {
    label: "Vertriebler-Pitch",
    prompt:
      "15s Reel für freie Vertriebler / Sales-Reps. Hook: 'Du verkaufst Versicherungen? Lass das.' Story: 25 % wiederkehrende Provision pro Händler den du an Allyver bringst. CTA: allyver.com/become-partner.",
  },
  {
    label: "Endkunden-Awareness",
    prompt:
      "15s Reel für lokale Käufer. Pattern-Interrupt: 'Stopp! Du kaufst noch immer alles bei Amazon?' Story: Allyver = lokal vor der Tür, faire Preise, ohne Lieferdienst-Aufschlag. Outro QR / URL.",
  },
  {
    label: "Restaurant-Owner Demo",
    prompt:
      "15s Reel: Demo für Restaurant-Besitzer. Hero: 'Dein Tisch — voll. Jeden Abend.' Drei Features mit Icons (rocket: mehr Reichweite / euro: kein Aufschlag / users: Stammgäste). Schluss: Allyver-Logo + URL.",
  },
];

export const AiPromptModal: React.FC<Props> = ({ open, onClose, onResult }) => {
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [model, setModel] = useState<string>("gpt-4o-mini");

  useEffect(() => {
    if (!open) return;
    setErr(null);
    void (async () => {
      try {
        const s = await api.aiStatus();
        setEnabled(s.enabled);
        setModel(s.model);
      } catch {
        setEnabled(false);
      }
    })();
  }, [open]);

  if (!open) return null;

  const generate = async (): Promise<void> => {
    setErr(null);
    if (prompt.trim().length < 4) {
      setErr("Bitte einen aussagekräftigen Prompt eingeben (min. 4 Zeichen).");
      return;
    }
    setBusy(true);
    try {
      const r = await api.aiGenerateReel({ prompt: prompt.trim(), targetSec: 15 });
      onResult(r.title, r.scenes);
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal ai-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h2>✨ Reel aus Prompt generieren</h2>
            <div className="muted small">
              Beschreibe das Video in natürlicher Sprache — die KI baut die Timeline.{" "}
              <span className="hint">Modell: {model}</span>
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {enabled === false && (
          <div className="warn-banner">
            <strong>OpenAI-Key fehlt.</strong> Lege <code>OPENAI_API_KEY</code> in deiner{" "}
            <code>.env</code> an (siehe <code>.env.example</code>) und starte den API-Server neu.
            <br />
            Key holen: <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer">platform.openai.com/api-keys</a>
            {" — "}gpt-4o-mini kostet ~0,1 ¢ pro Generation.
          </div>
        )}

        <textarea
          className="ai-textarea"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="z.B. 15s Reel für Endkunden in Berlin. Hook: versteckte Lieferdienst-Provisionen. Story: lokal kaufen via Allyver. CTA: allyver.com"
          rows={6}
          disabled={busy || enabled === false}
        />

        <div className="ai-examples">
          <div className="muted small">Beispiele zum Klick-Übernehmen:</div>
          <div className="ai-example-row">
            {EXAMPLES.map((ex) => (
              <button
                key={ex.label}
                className="chip"
                onClick={() => setPrompt(ex.prompt)}
                disabled={busy || enabled === false}
                title={ex.prompt}
              >
                {ex.label}
              </button>
            ))}
          </div>
        </div>

        {err && <div className="error-row">{err}</div>}

        <div className="modal-actions">
          <button className="btn ghost" onClick={onClose} disabled={busy}>
            Abbrechen
          </button>
          <button
            className="btn primary"
            onClick={() => void generate()}
            disabled={busy || enabled === false || prompt.trim().length < 4}
          >
            {busy ? "Generiere…" : "✨ Generate Reel"}
          </button>
        </div>
      </div>
    </div>
  );
};
